import {useMemo} from "react"
import {useStatsigClient} from "@statsig/react-bindings"
import Log from "@/utils/Log"
import {setExperimentGroup} from "@/utils/experiment-groups"

/**
 * Nomes de parâmetro aceitos pra carregar o nome da variante. São dois porque
 * os experimentos do console não seguem um nome só: o social_login_options usa
 * "variant" e o offer_details_highlight usa "params".
 *
 * Se nenhum dos dois existir, cai no groupName — que é o grupo atribuído de
 * fato pelo Statsig e não depende de convenção nenhuma de nomenclatura.
 */
const VARIANT_PARAMS = ["variant", "params"]

/**
 * Força uma variante por query string, pra não ter que esperar cair no
 * percentual do experimento durante o desenvolvimento: por exemplo
 * ?ab_offer_details_highlight=test
 *
 * Fica desligado em produção. E quando a variante é forçada, a exposure NÃO
 * é registrada — assim o teste manual nunca entra na contagem nem contamina
 * o resultado do experimento.
 */
const isOverrideEnabled = (): boolean =>
    process.env.NEXT_PUBLIC_ELASTIC_APM_ENVIRONMENT !== 'production'

const getOverride = <T extends string>(experimentName: string): T | null => {
    if (typeof window === 'undefined' || !isOverrideEnabled()) {
        return null
    }

    const forced = new URLSearchParams(window.location.search).get(`ab_${experimentName}`)
    if (!forced) {
        return null
    }

    Log.log('statsig_variant_forcada:', experimentName, forced, '(exposure nao registrada)')
    return forced as T
}

/**
 * Descobre em qual grupo de um experimento do Statsig o usuário caiu.
 *
 * Tenta primeiro os parâmetros de variante (ver VARIANT_PARAMS) e, se não
 * achar nenhum, usa o groupName. Isso evita a falha silenciosa mais fácil de
 * cometer aqui: se o nome do parâmetro no console não casar com o que o
 * código procura, o get() devolve o fallback e o experimento parece estar
 * 100% no controle — sem erro nenhum, em qualquer log.
 *
 * Retorna null enquanto o Statsig ainda está carregando, pra quem consome
 * decidir não renderizar nada até o grupo real ser conhecido (evita flash da
 * variante errada).
 *
 * Por que getExperiment() em vez do hook useExperiment(): o StatsigProvider
 * renderiza os children mesmo durante o carregamento (só troca por
 * loadingComponent se a gente passar um). Com useExperiment(), a chamada
 * aconteceria já no primeiro render e registraria uma exposure com o valor
 * default — o usuário entraria na contagem do grupo errado antes do grupo real
 * ser conhecido, e depois registraria de novo. Chamando só quando isLoading
 * é false, cada usuário gera uma exposure única e no grupo certo, que é o que
 * sustenta a contagem de quem viu controle x teste.
 */
const useExperimentVariant = <T extends string>(
    experimentName: string,
    defaultVariant: T,
    enabled: boolean = true
): T | null => {
    const {client, isLoading} = useStatsigClient()

    return useMemo(() => {
        // fora do escopo do experimento: devolve o default sem consultar o
        // Statsig, pra não gerar exposure de quem não participa
        if (!enabled) {
            return defaultVariant
        }

        const forced = getOverride<T>(experimentName)
        if (forced) {
            setExperimentGroup(experimentName, forced)
            return forced
        }

        if (isLoading) {
            return null
        }

        const experiment = client.getExperiment(experimentName)

        const doParametro = VARIANT_PARAMS
            .map(param => experiment.get(param, ''))
            .find(value => typeof value === 'string' && value !== '')

        const variante = doParametro || experiment.groupName

        if (!variante) {
            Log.log('statsig_variante_nao_encontrada:', experimentName, experiment.value, experiment.details?.reason)
            return defaultVariant
        }

        const grupo = String(variante).toLowerCase() as T
        setExperimentGroup(experimentName, grupo)

        return grupo
    }, [client, isLoading, experimentName, defaultVariant, enabled])
}

export default useExperimentVariant
